import { useCallback, useMemo, useRef, useState } from 'react';
import { FieldValues, get, Path, PathValue, useFormContext } from 'react-hook-form';
import { FormComponentProps } from '../types';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { VirtualScrollbar } from './VirtualScrollbar';

interface FormDropDownProps<T> extends FormComponentProps<T> {
  placeholder: string;
  mandatory?: boolean;
  title: string;
  elementsForDisplay: { title: string; value: PathValue<T, Path<T>> }[];
  classProps?: string;
  hideErrorControl?: boolean;
}

export const FormDropdown = <T extends FieldValues>(props: FormDropDownProps<T>) => {
  const {
    formState: { errors },
    setValue,
    watch,
    register,
    setFocus
  } = useFormContext<T>();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const listRef = useRef(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  const inputProps = register<Path<T>>(props.name);
  const errorForName = useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);

  const displayElements = useMemo(() => {
    const elements = props.elementsForDisplay;
    if (!props.mandatory && props.placeholder) {
      return [{ title: props.placeholder, value: undefined as PathValue<T, Path<T>> }, ...elements];
    }
    return elements.map(el => ({
      title: el.title,
      value: el.value as any
    }));
  }, [props.elementsForDisplay, props.mandatory, props.placeholder]);

  const fieldValue = watch(props.name);
  const selectedValue = useMemo(() => {
    const element = displayElements.find(element => element.value == fieldValue);
    if (element) {
      return element.title;
    }
    return fieldValue;
  }, [displayElements, fieldValue]);

  const [inputIsFocused, setInputIsFocused] = useState(false);

  const onElementSelect = (index: number) => {
    if (index <= displayElements.length) {
      setValue(props.name, displayElements[index].value as any, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
    setInputIsFocused(false);
  };

  const onInputClick = () => {
    if (props.readonly) return;
    setFocus(props.name);
  };

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };

  const renderElement = (index: number, element: { title: string; value: PathValue<T, Path<T>> }) => (
    <div
      key={index}
      onMouseEnter={() => setCurrentItemIndex(index)}
      className={`bg-interactive-bold-green-db-12 ${currentItemIndex === index ? 'bg-interactive-light-teal-100' : ''} ${element.title === props.placeholder ? 'text-secondary-deep-blue-30' : ''} cursor-pointer`}
      onMouseDown={() => {
        onElementSelect(index);
      }}
    >
      <div className="pl-3 pt-3 pb-3">
        <span>{element.title}</span>
      </div>
    </div>
  );

  const keyDownCallback = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      let nextIndex: number | null = null;
      if (event.key === 'ArrowDown') {
        nextIndex = currentItemIndex + 1;
        if (nextIndex >= displayElements.length) {
          nextIndex = 0;
        }
      } else if (event.key === 'ArrowUp') {
        nextIndex = currentItemIndex - 1;
        if (nextIndex < 0) {
          nextIndex = displayElements.length - 1;
        }
      } else if (event.key === 'Enter') {
        onElementSelect(currentItemIndex);
        event.preventDefault();
        return;
      }

      if (nextIndex !== null && virtuosoRef.current !== null) {
        virtuosoRef.current.scrollIntoView({
          index: nextIndex,
          behavior: 'smooth',
          done: () => {
            if (nextIndex === null) return;
            setCurrentItemIndex(nextIndex);
          }
        });
        event.preventDefault();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentItemIndex, setCurrentItemIndex, virtuosoRef]
  );

  const scrollerRef = useCallback(
    (element: any) => {
      if (element && inputIsFocused) {
        window.addEventListener('keydown', keyDownCallback as any);
        listRef.current = element;
      } else {
        window.removeEventListener('keydown', keyDownCallback as any);
      }
    },
    [inputIsFocused, keyDownCallback]
  );

  const backgroundColour = () => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (errorForName) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  };

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (errorForName) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (errorForName) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  return (
    <div className={`w-full relative ${props.classProps ?? ''}`}>
      <div
        className={`${backgroundColour()} ${borderColour()} border-b-2 w-full cursor-pointer pt-1 focus:outline-none`}
        tabIndex={props.readonly ? -1 : 0}
        onClick={onInputClick}
        onBlur={() => setInputIsFocused(false)}
        onFocus={() => {
          if (props.readonly) return;
          setInputIsFocused(true);
        }}
        ref={inputProps.ref}
      >
        <div className="flex flex-row justify-between px-3 py-1">
          <label className={`text-xs cursor-pointer ${labelTextColour()}`}>{props.title}</label>
          {props.mandatory && !props.readonly && <label className={`text-xs ${labelTextColour()} block`}>*</label>}
        </div>
        <div className="flex flex-row pr-5 pb-1 cursor-pointer">
          <p
            className={`pl-3 w-full truncate ${selectedValue === props.placeholder || (!selectedValue && props.placeholder) ? 'text-secondary-deep-blue-30' : ''} ${backgroundColour()} cursor-pointer`}
          >
            {selectedValue ? selectedValue : props.placeholder}
          </p>
          {!props.readonly && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="-translate-y-2 translate-x-1">
              <path d="M12 15L7 10H17L12 15Z" className={errorForName ? 'fill-feedback-red' : 'fill-solid-bold-green'} />
            </svg>
          )}
        </div>
      </div>
      {inputIsFocused && (
        <div className={`block z-10 absolute w-full bg-interactive-bold-green-db-12 border-solid-bold-green border-[1px]`}>
          <div className="resize-y overflow-hidden" style={{ height: displayElements.length <= 5 ? displayElements.length * 50 : 300 }}>
            <Virtuoso
              ref={virtuosoRef}
              tabIndex={-1}
              data={displayElements}
              itemContent={renderElement}
              scrollerRef={scrollerRef}
              style={{ height: '100%' }}
              // style={{ height: displayElements.length <= 5 ? displayElements.length * 50 : 300 }}
              components={{
                Scroller: VirtualScrollbar
              }}
            />
          </div>
        </div>
      )}
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
